'use client';

import type {ChangeEvent} from 'react';
import {
	type FC,
	type InputHTMLAttributes,
	useCallback,
	useEffect,
	useId,
	useState,
} from 'react';
import clsx from 'clsx';

import {searchInputClasses} from '@/components/input';

export const NumberInput: FC<InputHTMLAttributes<HTMLInputElement>> = ({
	className,
	...props
}) => {
	const id = useId();
	const [value, setValue] = useState<number>(0);

	useEffect(() => {
		// FIXME: fix react hook error
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setValue(
			typeof props.value === 'number'
				? props.value
				: typeof props.value === 'string'
					? parseInt(props.value)
					: 0,
		);
	}, [props.value]);

	const decrement = useCallback(() => {
		const min =
			props.min === undefined
				? -Infinity
				: typeof props.min === 'string'
					? parseInt(props.min)
					: props.min;
		if (value - 1 < min) {
			return;
		}
		setValue(value - 1);
	}, [props.min, value]);

	const increment = useCallback(() => {
		const max =
			props.max === undefined
				? Infinity
				: typeof props.max === 'string'
					? parseInt(props.max)
					: props.max;
		if (value + 1 > max) {
			return;
		}
		setValue(value + 1);
	}, [props.max, value]);

	const updateValue = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const min =
				props.min === undefined
					? -Infinity
					: typeof props.min === 'string'
						? parseInt(props.min)
						: props.min;
			const max =
				props.max === undefined
					? Infinity
					: typeof props.max === 'string'
						? parseInt(props.max)
						: props.max;
			const newValue = parseInt(event.target.value);
			if (newValue < min) {
				setValue(min);
			} else if (newValue > max) {
				setValue(max);
			} else {
				setValue(newValue);
			}
		},
		[props.max, props.min],
	);

	return (
		<div className={clsx('relative', 'w-32')}>
			<div
				className={clsx(
					'absolute',
					'h-full',
					'top-0',
					'left-2',
					'flex',
					'items-center',
					'justify-center',
				)}
			>
				<button
					type="button"
					className={clsx(
						'aspect-square',
						'h-8',
						'w-8',
						'flex',
						'items-center',
						'justify-center',
						'text-bodyBold',
						'rounded-full',
						'focus:text-pBold',
					)}
					onClick={decrement}
				>
					-
				</button>
			</div>

			<input
				id={id}
				type="number"
				className={clsx(
					searchInputClasses,
					className,
					'!rounded-[3.5rem]',
				)}
				onChange={updateValue}
				{...props}
				value={value}
			/>

			<div
				className={clsx(
					'absolute',
					'h-full',
					'top-0',
					'right-2',
					'flex',
					'items-center',
					'justify-center',
				)}
			>
				<button
					type="button"
					className={clsx(
						'h-8',
						'w-8',
						'p-1',
						'flex',
						'items-center',
						'justify-center',
						'text-bodyBold',
						'rounded-full',
						'focus:text-pBold',
					)}
					onClick={increment}
				>
					+
				</button>
			</div>
		</div>
	);
};
